import {LegacyWBIcon} from '@wandb/weave/common/components/elements/LegacyWBIcon';
import * as globals from '@wandb/weave/common/css/globals.styles';
import {clsx} from 'clsx';
import React, {type JSX} from 'react';

import {useSetHighlight} from '../../state/interactState/hooks';
import {legendTemplateRemoveCrosshairValues} from '../../util/legend';
import {getLegendOverrideKey} from '../../util/plotHelpers/legend';
import {TruncateTextTooltip} from '../common/TruncateText/TruncateTextTooltip';
import {TruncationType} from '../common/TruncateText/TruncateTextTypes';
import {markToIconNameMap} from '../LineStylePicker';
import {useSingleRunModeContext} from '../MultiRunWorkspace/SingleRunMode';
import {useWorkspaceSettingsColorPalette} from '../WorkspaceDrawer/Settings/colorPalette/ColorPalette';
import {ColorPaletteOption, getTextColor} from './../../util/colors';
import {
  type PlotFontSize,
  plotFontSizeToPx,
} from './../../util/plotHelpers/plotFontSize';
import {type Line} from './../../util/plotHelpers/types';
import {usePanelConfigContext} from './PanelConfigContext';

type LinePlotLegendProps = {
  children?: React.ReactNode;
  disableRunLinks?: boolean;
  expandLegend?: boolean;
  fontSize: PlotFontSize;
  hiddenLines: Line[];
  isVertical: boolean;
  legendPrefix?: React.Component | JSX.Element;
  lines: Line[];
  disallowSelect: boolean;
};
export const LinePlotLegend = React.memo(
  ({
    disableRunLinks,
    disallowSelect,
    fontSize,
    hiddenLines,
    isVertical,
    lines,
  }: LinePlotLegendProps) => {
    const {isSingleRun} = useSingleRunModeContext();
    const setHighlightRun = useSetHighlight();
    const fSize = plotFontSizeToPx.legend[fontSize];
    const {isMultiMetric} = usePanelConfigContext();

    const wrapperClasses = clsx(
      `scrollbar-hide flex flex-shrink-0 flex-wrap gap-x-8 gap-y-2 leading-none`,
      {
        // tailwind requires the classes to be fully built for static analysis
        // https://tailwindcss.com/docs/content-configuration#dynamic-class-names
        'text-[10px]': fSize === 10,
        'text-[12px]': fSize === 12,
        'text-[14px]': fSize === 14,
        'flex-column w-full items-center justify-center': isVertical,
        'flex-row justify-start': !isVertical,
        'select-none': disallowSelect,
      }
    );

    const hiddenLineIds = React.useMemo(
      () => new Set(hiddenLines.map(l => getLegendOverrideKey(l))),
      [hiddenLines]
    );

    const colorPaletteOption = useWorkspaceSettingsColorPalette();
    const {colorRunNames} = usePanelConfigContext();

    return (
      <div className={wrapperClasses} data-test="line-plot-legend">
        {lines.map((line, i) => {
          const hidden = hiddenLineIds.has(getLegendOverrideKey(line));
          const color = !hidden ? line.color : globals.gray500;

          // Full, untruncated legend text (run name + any extra legend fields),
          // with the crosshair value template (e.g. `[[ ${x}: ${y} ]]`) removed.
          const fullText = legendTemplateRemoveCrosshairValues(
            line.title ?? ''
          );

          // Reproduce the run link FancyLegend used to build, so clicking the
          // legend name still navigates to the run when links are enabled.
          const fancyTitle = line.fancyTitle;
          const isGrouped = (fancyTitle?.groupKeys?.length ?? 0) > 0;
          const runLink =
            !disableRunLinks &&
            !isGrouped &&
            fancyTitle?.rootUrl != null &&
            fancyTitle?.run?.name != null
              ? `${fancyTitle.rootUrl}/${fancyTitle.run.name}`
              : undefined;

          return (
            <div
              className={`flex items-center max-w-full min-w-0 ${
                isVertical ? '' : 'basis-full'
              }`}
              // oxlint-disable-next-line react/no-array-index-key -- https://oxc.rs/docs/guide/usage/linter/rules/react/no-array-index-key.html
              key={`${line.uniqueId}-${i}`}>
              <span
                className="line-plot-color flex items-center"
                style={{color}}>
                <LegacyWBIcon name={markToIconNameMap[line.mark ?? 'solid']} />
              </span>
              <div
                data-test="line-plot-legend-run-name"
                className="min-w-0 flex-auto overflow-hidden text-wb-text-secondary"
                onMouseOver={() => {
                  if (!isSingleRun) {
                    setHighlightRun(
                      'run:name',
                      line.uniqueId,
                      isMultiMetric ? line.metricName : undefined
                    );
                  }
                }}
                onMouseOut={() => {
                  if (!isSingleRun) {
                    setHighlightRun('run:name', undefined, undefined);
                  }
                }}
                style={{
                  color:
                    colorPaletteOption === ColorPaletteOption.DEFAULT &&
                    colorRunNames
                      ? getTextColor(color)
                      : undefined,
                }}>
                <TruncateTextTooltip
                  className="text-left"
                  fullText={fullText}
                  truncationType={TruncationType.Middle}
                  linkPath={runLink}
                  noLink={runLink == null}
                />
              </div>
            </div>
          );
        })}
      </div>
    );
  }
);

LinePlotLegend.displayName = 'LinePlotLegend';
